import React from 'react';
import { ExchangeRatesPanel } from '../components/panels/monetary/ExchangeRatesPanel';
import { InterestRatesPanel } from '../components/panels/monetary/InterestRatesPanel';
import { BankingSectorPanel } from '../components/panels/monetary/BankingSectorPanel';
import { ReservesPanel } from '../components/panels/monetary/ReservesPanel';
import { CreditRatingsPanel } from '../components/panels/monetary/CreditRatingsPanel';
import { usePanelManager, PanelConfig } from '../hooks/usePanelManager';
import { MinimizedBar } from '../components/ui/MinimizedBar';

// Panel configuration for Monetary & Financial view
const panelConfig: PanelConfig[] = [
  { id: 'exchange-rates', title: 'Exchange Rates', row: 1, colSpan: 2 },
  { id: 'credit-ratings', title: 'Credit Ratings', row: 1, colSpan: 2 },
  { id: 'interest-rates', title: 'Interest Rates', row: 2 },
  { id: 'reserves', title: 'Reserves', row: 2 },
  { id: 'banking-sector', title: 'Banking Sector', row: 2, colSpan: 2 },
];

// Panel component map
const panelComponents: Record<string, React.FC<{ isMinimized?: boolean; onMinimize?: () => void }>> = {
  'exchange-rates': ExchangeRatesPanel,
  'interest-rates': InterestRatesPanel,
  'banking-sector': BankingSectorPanel,
  'reserves': ReservesPanel,
  'credit-ratings': CreditRatingsPanel,
};

const MonetaryFinancialView: React.FC = () => {
  const {
    minimizedPanelsList,
    handleMinimize,
    handleRestore,
    getOrderedPanels,
    isPanelMinimized,
    getDragState,
    getDragProps,
  } = usePanelManager(panelConfig);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <MinimizedBar panels={minimizedPanelsList} onRestore={handleRestore} />

      <div className="flex-1 flex flex-col md:grid md:grid-cols-2 lg:grid-cols-4 md:grid-rows-[1fr_1fr] gap-px p-px bg-terminal-border overflow-y-auto md:overflow-hidden">
        {/* Row 1 - Exchange Rates & Credit Ratings */}
        {getOrderedPanels(1).map(panel => (
          <div
            key={panel.id}
            {...getDragProps(panel.id)}
            className={`min-h-[250px] sm:min-h-[300px] md:min-h-0 col-span-1 lg:col-span-2 h-full transition-all duration-200 ${
              getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
            } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
          >
            {React.createElement(panelComponents[panel.id], {
              isMinimized: isPanelMinimized(panel.id),
              onMinimize: () => handleMinimize(panel.id),
            })}
          </div>
        ))}

        {/* Row 2 - Interest Rates, Reserves, Banking Sector */}
        {getOrderedPanels(2).map(panel => (
          <div
            key={panel.id}
            {...getDragProps(panel.id)}
            className={`min-h-[250px] sm:min-h-[300px] md:min-h-0 ${
              panel.colSpan === 2 ? 'col-span-1 lg:col-span-2' : ''
            } h-full transition-all duration-200 ${
              getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
            } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
          >
            {React.createElement(panelComponents[panel.id], {
              isMinimized: isPanelMinimized(panel.id),
              onMinimize: () => handleMinimize(panel.id),
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MonetaryFinancialView;
